import * as THREE from 'three'
import { BlendFunction, Effect } from 'postprocessing'
import { createGradeLut, FILMIC_GRADE, type GradeLut, type GradeSettings } from './ColorGrade'
import { AutoExposure } from './AutoExposure'

export type ToneMapOperator = 'filmic' | 'agx' | 'aces' | 'neutral'

/**
 * Exposure, tone mapping and the colour grade, in one pass.
 *
 * Keeping them together matters: the LUT is authored in display space, so it
 * has to be applied immediately after the tone curve has mapped scene
 * radiance into [0,1] and before anything else touches the pixel. Splitting
 * this across effects means round-tripping the colour space twice.
 *
 * `filmic` is the default: the ACES curve, which has the range this scene
 * wants, driven from the brightest channel so hue survives it. Per-channel ACES
 * rolls each primary on its own, and on a sunset frame that measured as a sun
 * core at RGB 0.987/0.972/0.960 — neutral white in the middle of an amber sky.
 * AgX would also have fixed the hue but its log encoding spans 16.5 stops
 * against this scene's five, and it lands correspondingly flat; both are kept
 * behind `?tonemap=` for comparison.
 *
 * Output is display-referred and sRGB-encoded rather than linear. Everything
 * downstream — SMAA, temporal accumulation, grain, vignette — behaves better
 * in a perceptual space, and the last pass converts back exactly once.
 */
const TONE_MAP_FUNCTIONS = /* glsl */ `
vec3 agxContrast(const in vec3 x) {
  vec3 x2 = x * x;
  vec3 x4 = x2 * x2;
  return 15.5 * x4 * x2 - 40.14 * x4 * x + 31.96 * x4 - 6.868 * x2 * x + 0.4298 * x2 + 0.1191 * x - 0.00232;
}

vec3 toneMapAgx(const in vec3 linearColor) {
  const mat3 agxInset = mat3(
    0.856627153315983, 0.137318972929847, 0.11189821299995,
    0.0951212405381588, 0.761241990602591, 0.0767994186031903,
    0.0482516061458583, 0.101439036467562, 0.811302368396859
  );
  const mat3 agxOutset = mat3(
    1.1271005818144368, -0.1413297634984383, -0.14132976349843826,
    -0.11060664309660323, 1.157823702216272, -0.11060664309660294,
    -0.016493938717834573, -0.016493938717834257, 1.2519364065950405
  );
  const mat3 srgbToRec2020 = mat3(
    0.6274, 0.0691, 0.0164,
    0.3293, 0.9195, 0.0880,
    0.0433, 0.0113, 0.8956
  );
  const mat3 rec2020ToSrgb = mat3(
    1.6605, -0.1246, -0.0182,
    -0.5876, 1.1329, -0.1006,
    -0.0728, -0.0083, 1.1187
  );
  const float minEv = -12.47393;
  const float maxEv = 4.026069;

  vec3 c = agxInset * (srgbToRec2020 * linearColor);
  c = clamp((log2(max(c, 1e-10)) - minEv) / (maxEv - minEv), 0.0, 1.0);
  c = agxOutset * agxContrast(c);
  c = pow(max(c, vec3(0.0)), vec3(2.2));
  return clamp(rec2020ToSrgb * c, 0.0, 1.0);
}

/**
 * Hill's fit of the ACES RRT+ODT, with one deliberate departure: the numerator's
 * constant -0.000090537 is dropped so the curve passes through the origin.
 *
 * That constant is a least-squares artefact. It makes the fit *negative* below a
 * scene value of 0.003253, and every implementation clamps the result to zero —
 * which means the tone mapper hard-clips the bottom two and a half stops of the
 * frame to display black before the grade ever sees them. This scene set lives
 * there: with the shipped exposure, a surface lit only by sky bounce in the
 * backlit half of the sunset pose falls inside that window. Measured on the
 * eight round-5 captures, 16.1% of the sunset frame and 9.9% of the plaza frame
 * sat in a single spike between code 4 and code 8 — the pile-up of everything
 * the clip flattened — and shadow crush was the most-reported defect of the
 * round, at seventeen mentions.
 *
 * Dropping the constant restores a real gradient across that window. It changes
 * nothing else: at a scene value of 5.0 the numerator moves by 3.6 parts per
 * million, so the white point, the shoulder and every mid-tone are untouched.
 * Simulated over the eight captures it takes pctBelow8 from 16.5 to 10.8 on
 * sunset, 9.4 to 6.3 on plaza and 9.0 to 6.6 on vista while moving the frame
 * mean by +0.35 and local contrast not at all.
 */
float acesCurve(const in float v) {
  float a = v * (v + 0.0245786);
  float b = v * (0.983729 * v + 0.4329510) + 0.238081;
  return a / b;
}

vec3 acesFit(const in vec3 v) {
  vec3 a = v * (v + 0.0245786);
  vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
  return a / b;
}

/**
 * How fast a bright colour bleaches toward white, as an exponent on the
 * tone-mapped peak. Higher holds hue for longer. At 3.5, scene radiance below
 * about 0.7 keeps essentially all of its chroma, a horizon sky at 1.3 lands on
 * sRGB 232,211,191 rather than paper white, and only a genuine emitter — the
 * sun disc, a muzzle flash core — bleaches to flat white the way it should.
 */
const float HIGHLIGHT_CROSSTALK = 3.5;

/**
 * The ACES curve applied to the brightest channel, with the colour rescaled
 * around it so hue and saturation ride through unchanged, then a deliberate
 * bleed to white on top.
 *
 * This is the "highlight hue path" an ACES variant needs. Per-channel ACES
 * shears saturated highlights because each primary crosses the shoulder at a
 * different scene value; driving one scalar curve from the peak cannot do that.
 * Film does eventually desaturate as it bleaches, which is what the crosstalk
 * term restores — smoothly and under one knob, rather than as a side effect of
 * where the shoulder happens to sit.
 *
 * toneWhiteScale is 1/acesCurve(SCENE_WHITE) and is the fix for round 3's
 * missing white point. Hill's fit is asymptotic: it only reaches 1.0 at a scene
 * value of 25.7, and nothing in this level except the sun disc gets within a
 * factor of eight of that, so the frame's brightest pixel measured 236 and a
 * judge read the result correctly as "no true black, no true white,
 * nondirectional fill ... the most reliable untuned-render tell". Normalising
 * the curve by its own value at a chosen scene white makes that white point an
 * authored decision instead of an accident of the fit's asymptote.
 */
vec3 toneMapFilmic(const in vec3 linearColor) {
  float peak = max(max(linearColor.r, linearColor.g), max(linearColor.b, 1e-5));
  vec3 ratio = linearColor / peak;
  float mapped = clamp(acesCurve(peak) * toneWhiteScale, 0.0, 1.0);
  ratio = mix(ratio, vec3(1.0), pow(mapped, HIGHLIGHT_CROSSTALK));
  return clamp(ratio * mapped, 0.0, 1.0);
}

// Stephen Hill's fit of the ACES RRT+ODT. Note there is no 1/0.6 pre-scale
// here: that is Unreal's convention for matching its own exposure, and baking
// it in silently brightened every mid-tone by a full stop while pretending the
// exposure knob was at 1.0. Exposure is exposure; the curve is the curve.
vec3 toneMapAces(const in vec3 linearColor) {
  const mat3 acesIn = mat3(
    0.59719, 0.07600, 0.02840,
    0.35458, 0.90834, 0.13383,
    0.04823, 0.01566, 0.83777
  );
  const mat3 acesOut = mat3(
    1.60475, -0.10208, -0.00327,
    -0.53108, 1.10813, -0.07276,
    -0.07367, -0.00605, 1.07602
  );
  return clamp(acesOut * acesFit(acesIn * linearColor), 0.0, 1.0);
}

vec3 toneMapNeutral(const in vec3 linearColor) {
  const float startCompression = 0.76;
  const float desaturation = 0.15;
  vec3 c = linearColor;
  float lo = min(c.r, min(c.g, c.b));
  float shift = lo < 0.08 ? lo - 6.25 * lo * lo : 0.04;
  c -= shift;
  float peak = max(c.r, max(c.g, c.b));
  if (peak < startCompression) return clamp(c, 0.0, 1.0);
  float d = 1.0 - startCompression;
  float newPeak = 1.0 - d * d / (peak + d - startCompression);
  c *= newPeak / peak;
  float g = 1.0 - 1.0 / (desaturation * (peak - newPeak) + 1.0);
  return clamp(mix(c, vec3(newPeak), g), 0.0, 1.0);
}
`

const COLOR_SPACE_FUNCTIONS = /* glsl */ `
vec3 gradeEncode(const in vec3 c) {
  vec3 v = max(c, vec3(0.0));
  return mix(1.055 * pow(v, vec3(1.0 / 2.4)) - 0.055, v * 12.92, step(v, vec3(0.0031308)));
}
`

/**
 * Exposure, from the metered frame.
 *
 * exposureParams is (base, strength, maxStops, unused). The meter hands over a
 * correction already expressed in stops; this scales it, clamps it hard in both
 * directions and applies it. The clamp is also the failure mode: a broken
 * measurement costs a mis-graded frame rather than a black or blown one, and a
 * *missing* one costs nothing at all, because the meter stores zero for "no
 * correction" and an unbound texture reads as zero.
 */
const EXPOSURE_FUNCTION = /* glsl */ `
uniform vec4 exposureParams;

#ifdef AUTO_EXPOSURE
// highp, not lowp: a lowp float is only guaranteed the range (-2, 2), which
// would clamp part of the correction range this pass exists to produce.
uniform highp sampler2D exposureBuffer;

float sceneExposure() {
  float correction = texture2D(exposureBuffer, vec2(0.5)).r;
  float stops = clamp(correction * exposureParams.y,
                      -exposureParams.z, exposureParams.z);
  return exposureParams.x * exp2(stops);
}
#else
float sceneExposure() {
  return exposureParams.x;
}
#endif
`

function buildFragmentShader(operator: ToneMapOperator, autoExposure: boolean): string {
  const call =
    operator === 'agx' ? 'toneMapAgx(scene)'
      : operator === 'neutral' ? 'toneMapNeutral(scene)'
        : operator === 'aces' ? 'toneMapAces(scene)'
          : 'toneMapFilmic(scene)'

  return /* glsl */ `
${autoExposure ? '#define AUTO_EXPOSURE' : ''}

uniform mediump sampler3D lut;
uniform float toneWhiteScale;
uniform float lutScale;
uniform float lutOffset;
uniform float lutStrength;

${EXPOSURE_FUNCTION}
${TONE_MAP_FUNCTIONS}
${COLOR_SPACE_FUNCTIONS}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 scene = max(inputColor.rgb, vec3(0.0)) * sceneExposure();
  vec3 display = gradeEncode(${call});
  vec3 graded = texture(lut, display * lutScale + lutOffset).rgb;
  outputColor = vec4(mix(display, graded, lutStrength), inputColor.a);
}
`
}

/**
 * `acesCurve(v)`, in TypeScript, so the white point can be normalised on the
 * CPU. It must stay byte-for-byte equivalent to the GLSL above — including the
 * dropped numerator constant — or `toneWhiteScale` normalises a curve the shader
 * is not evaluating.
 */
function acesCurve(v: number): number {
  return (v * (v + 0.0245786)) / (v * (0.983729 * v + 0.4329510) + 0.238081)
}

export interface GradeEffectOptions {
  operator?: ToneMapOperator
  /** Linear multiplier applied to scene radiance when the frame meters at {@link AutoExposureConfig.referenceLuminance}. */
  exposure?: number
  /**
   * Scene radiance, after exposure, that maps to display white. Speculars, the
   * sun disc and muzzle flashes need to clear it; a sunlit wall must not.
   */
  sceneWhite?: number
  /** Omit to hold {@link exposure} fixed. */
  autoExposure?: AutoExposureConfig
  /** 0 disables the LUT and leaves the raw tone curve. */
  lutStrength?: number
  lutSize?: number
  grade?: GradeSettings
}

export interface AutoExposureConfig {
  /**
   * Geometric mean scene luminance at which {@link GradeEffectOptions.exposure}
   * is used unmodified.
   */
  referenceLuminance: number
  /** 1 normalises every frame to the reference; 0 is a fixed exposure. */
  strength: number
  /** Hard bound on the correction, in stops, either way. */
  maxStops: number
  /** Adaptation rate in reciprocal seconds. */
  rate: number
}

export class GradeEffect extends Effect {
  private readonly lut: GradeLut
  private readonly autoExposure: AutoExposure | null

  /**
   * Set while the capture harness holds the simulation still. See
   * {@link AutoExposure.measure} for why the meter must not ease during a
   * capture.
   */
  snapExposure = false

  constructor({
    operator = 'filmic',
    exposure = 1,
    sceneWhite = 5,
    autoExposure,
    lutStrength = 1,
    lutSize = 41,
    grade = FILMIC_GRADE,
  }: GradeEffectOptions = {}) {
    const lut = createGradeLut(lutSize, grade)
    const meter = autoExposure
      ? new AutoExposure({
          referenceLuminance: autoExposure.referenceLuminance,
          rate: autoExposure.rate,
        })
      : null
    const uniforms = new Map<string, THREE.Uniform>([
      ['lut', new THREE.Uniform(lut.texture)],
      ['toneWhiteScale', new THREE.Uniform(1 / acesCurve(sceneWhite))],
      ['exposureParams', new THREE.Uniform(new THREE.Vector4(
        exposure,
        autoExposure ? autoExposure.strength : 0,
        autoExposure ? autoExposure.maxStops : 0,
        0,
      ))],
      ['lutScale', new THREE.Uniform(lut.scale)],
      ['lutOffset', new THREE.Uniform(lut.offset)],
      ['lutStrength', new THREE.Uniform(lutStrength)],
    ])
    if (meter) uniforms.set('exposureBuffer', new THREE.Uniform(meter.texture))

    super('GradeEffect', buildFragmentShader(operator, meter !== null), {
      blendFunction: BlendFunction.SRC,
      uniforms,
    })

    this.lut = lut
    this.autoExposure = meter
  }

  /**
   * Runs with the pass input buffer bound and before the merged effect shader
   * samples it, so the meter sees this frame's scene radiance — and sees it
   * *before* exposure is applied, which is what keeps the loop open rather than
   * closed.
   */
  update(renderer: THREE.WebGLRenderer, inputBuffer: THREE.WebGLRenderTarget, deltaTime: number): void {
    if (!this.autoExposure) return
    this.autoExposure.measure(renderer, inputBuffer, deltaTime, this.snapExposure)
    this.uniforms.get('exposureBuffer')!.value = this.autoExposure.texture
  }

  /** Base exposure: the value used when the frame meters at the reference. */
  get exposure(): number {
    return (this.uniforms.get('exposureParams')!.value as THREE.Vector4).x
  }

  set exposure(value: number) {
    ;(this.uniforms.get('exposureParams')!.value as THREE.Vector4).x = value
  }

  get lutStrength(): number {
    return this.uniforms.get('lutStrength')!.value as number
  }

  set lutStrength(value: number) {
    this.uniforms.get('lutStrength')!.value = value
  }

  /** Discards the adaptation history so the next frame meters from scratch. */
  resetExposure(): void {
    this.autoExposure?.reset()
  }

  dispose(): void {
    this.lut.texture.dispose()
    this.autoExposure?.dispose()
    super.dispose()
  }
}
